import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiQWorkardsEnv } from '@qaroni-core/config/apis/api-qworkards/api-qworkards.config';
import {
  EmailCaptchaJson,
  LoginJson,
  ResetPasswordJson,
  SignUpJson,
  ValidateOtpJson,
  ValidateRegisterJson,
} from '../types/authentication';
import { OAuthApiObject } from '../types/o-auth-response';
import { OtpValidationApiObject } from '../types/otp-validation-response';
import { SocialAuthLinkApiObject } from '../types/social-auth-link-response';
import { UserApiObject } from '../types/user-response';

@Injectable({
  providedIn: 'root',
})
export class OAuthHttpService {
  private http = inject(HttpClient);

  public register$(signUpJson: SignUpJson) {
    const url = `${ApiQWorkardsEnv.baseUrl}/registers`;

    return this.http.post<UserApiObject>(url, signUpJson, {
      observe: 'response',
    });
  }

  public validate$(userID: number, validateRegisterJson: ValidateRegisterJson) {
    const url = `${ApiQWorkardsEnv.baseUrl}/users/${userID}/validates`;

    return this.http.patch<OAuthApiObject>(url, validateRegisterJson, {
      observe: 'response',
    });
  }

  public login$(loginJSON: LoginJson) {
    const url = `${ApiQWorkardsEnv.baseUrl}/logins`;

    return this.http.post<OAuthApiObject>(url, loginJSON, {
      observe: 'response',
    });
  }

  public loginWithGoogleAccount$() {
    const url = `${ApiQWorkardsEnv.baseUrl}/socialites/google/auth`;

    return this.http.get<SocialAuthLinkApiObject>(url, {
      observe: 'response',
    });
  }

  public signUpWithGoogleAccount$() {
    const url = `${ApiQWorkardsEnv.baseUrl}/socialites/google/registers`;

    return this.http.get<SocialAuthLinkApiObject>(url, {
      observe: 'response',
    });
  }

  public loginWithMicrosoftAccount$(token: string) {
    const url = `${ApiQWorkardsEnv.baseUrl}/socialites/microsoft/logins/callback/apps`;

    return this.http.post<OAuthApiObject>(
      url,
      { token },
      { observe: 'response' }
    );
  }

  public forgot$(emailCaptchaJson: EmailCaptchaJson) {
    const url = `${ApiQWorkardsEnv.baseUrl}/forgots`;

    return this.http.post<UserApiObject>(url, emailCaptchaJson, {
      observe: 'response',
    });
  }

  public resetPassword$(userID: number, resetPasswordJSON: ResetPasswordJson) {
    const url = `${ApiQWorkardsEnv.baseUrl}/users/${userID}/passwords/resets/temps`;

    return this.http.patch<OAuthApiObject>(url, resetPasswordJSON, {
      observe: 'response',
    });
  }

  public validateOTP$(userID: number, validateOtpJson: ValidateOtpJson) {
    const url = `${ApiQWorkardsEnv.baseUrl}/users/${userID}/otp/validates/temps`;

    return this.http.post<OtpValidationApiObject>(url, validateOtpJson, {
      observe: 'response',
    });
  }

  public getUser$(userId: number) {
    const url = `${ApiQWorkardsEnv.baseUrl}/users/${userId}`;

    return this.http.get<UserApiObject>(url, { observe: 'response' });
  }

  public validateEmail$(emailCaptchaJson: EmailCaptchaJson) {
    const url = `${ApiQWorkardsEnv.baseUrl}/validates/emails`;

    return this.http.post<UserApiObject>(url, emailCaptchaJson, {
      observe: 'response',
    });
  }

  public sendMagicLink$(email: string) {
    const url = `${ApiQWorkardsEnv.baseUrl}/magics/tokens`;

    return this.http.post<UserApiObject>(
      url,
      { email },
      { observe: 'response' }
    );
  }

  public validateMagicLink$(token: string, signature: string) {
    const url = `${ApiQWorkardsEnv.baseUrl}/magics/tokens/${token}`;

    return this.http.get<OAuthApiObject>(url, {
      observe: 'response',
      params: { signature },
    });
  }

  public sendRegisterLink$(
    invitationId: number,
    expires: string,
    signature: string,
    appCode: string
  ) {
    const url = `${ApiQWorkardsEnv.baseUrl}/invitations/${invitationId}/validates`;

    return this.http.get<UserApiObject>(url, {
      observe: 'response',
      params: { expires, signature, 'App-Code': appCode },
    });
  }
}
